import React from 'react';
import { Server, ShieldCheck, Activity, Database, ArrowRight, ChevronRight, Home, Zap, Lock, Brain, Network, Settings } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { SEO } from '../components/SEO';
const solutionKeys = ['ai', 'gov', 'defense', 'data'];
const solutionIcons = [<Server className="w-8 h-8 text-ai-teal" data-id="element-444" />, <Activity className="w-8 h-8 text-muted-gold" data-id="element-445" />, <ShieldCheck className="w-8 h-8 text-neural-green" data-id="element-446" />, <Database className="w-8 h-8 text-white" data-id="element-447" />];
const strategicSolutions = [{
  icon: Home,
  title: 'Intelligent Living Systems',
  description: 'Advanced cyber-physical systems that create adaptive, secure, and context-aware living environments. Designed to enhance safety, efficiency, and quality of life through artificial intelligence—without intrusive surveillance.',
  color: 'ai-teal'
}, {
  icon: Zap,
  title: 'Critical Infrastructure Systems',
  description: 'Resilient intelligence layers for essential infrastructure at urban and national scale. Built to monitor, protect, and optimize energy, water, transportation, and utilities under continuous and extreme conditions.',
  color: 'neural-green'
}, {
  icon: Lock,
  title: 'Defense & Security Systems',
  description: 'Human-in-the-loop security architectures for strategic assets and sensitive environments. Focused on situational awareness, controlled autonomy, and accountability—ensuring protection without surrendering human authority.',
  color: 'muted-gold'
}, {
  icon: Brain,
  title: 'Ethical AI & Decision Support',
  description: 'Artificial intelligence systems designed to assist human judgment, not replace it. Enabling analysis, simulation, and prediction within clearly defined ethical and governance boundaries.',
  color: 'white'
}, {
  icon: Network,
  title: 'Sovereign AI & Cyber-Physical Infrastructure',
  description: 'Private, on-premise, and air-gapped AI infrastructure integrated with physical systems. Ensuring data sovereignty, secure intelligence, and real-time orchestration across critical environments.',
  color: 'ai-teal'
}, {
  icon: Settings,
  title: 'Bespoke Technology Systems',
  description: 'Mission-specific technology systems architected for unique operational, regulatory, and strategic contexts. Each system is custom-built—modular, scalable, and aligned with long-term institutional objectives.',
  color: 'muted-gold'
}];
export function Solutions() {
  const {
    t,
    language
  } = useLanguage();
  const seoTitles = {
    en: 'Strategic Solutions - Sovereign AI & Cyber Defense | NEW ASIA TECH',
    id: 'Solusi Strategis - AI Berdaulat & Pertahanan Cyber | NEW ASIA TECH'
  };
  const seoDescriptions = {
    en: 'NEW ASIA TECH strategic solutions: Sovereign AI Infrastructure, Predictive Governance Systems, Critical Asset Defense, Strategic Data Vaults. Enterprise technology for governments and institutions.',
    id: 'Solusi strategis NEW ASIA TECH: Infrastruktur AI Berdaulat, Sistem Tata Kelola Prediktif, Pertahanan Aset Kritis, Brankas Data Strategis. Teknologi enterprise untuk pemerintah dan institusi.'
  };
  const seoKeywords = {
    en: 'sovereign AI, AI infrastructure, predictive governance, smart city solutions, cybersecurity, critical infrastructure protection, data vault, enterprise AI, government technology, NEW ASIA TECH solutions',
    id: 'AI berdaulat, infrastruktur AI, tata kelola prediktif, solusi kota pintar, keamanan siber, perlindungan infrastruktur kritis, brankas data, AI enterprise, teknologi pemerintah, solusi NEW ASIA TECH'
  };
  return <>
      <SEO title={seoTitles[language]} description={seoDescriptions[language]} keywords={seoKeywords[language]} url="/solutions" type="website" data-id="element-448" />

      <div className="min-h-screen pt-32 pb-20 px-6" data-id="element-449">
        <div className="max-w-[1800px] mx-auto" data-id="element-450">
          <header className="mb-20 animate-fade-in-up" data-id="element-451">
            <h1 className="text-5xl md:text-7xl font-display font-bold text-ivory mb-6" data-id="element-452">
              {t('solutions.title')}
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl border-l-2 border-ai-teal pl-6" data-id="element-453">
              {t('solutions.subtitle')}
            </p>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-32" data-id="element-454">
            {solutionKeys.map((key, idx) => <article key={idx} className={`group border border-white/10 bg-carbon p-10 hover:border-ai-teal/30 hover:shadow-2xl hover:shadow-ai-teal/5 transition-all duration-500 animate-fade-in-up delay-${idx * 100} relative overflow-hidden`} data-id="element-455">
                <div className="absolute top-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-456">
                  <ArrowRight className="text-ai-teal -rotate-45 group-hover:rotate-0 transition-transform duration-500" data-id="element-457" />
                </div>

                <div className="mb-8 p-4 bg-white/5 inline-block rounded-sm border border-white/5 group-hover:border-ai-teal/50 transition-colors duration-300" data-id="element-458">
                  {solutionIcons[idx]}
                </div>
                <h2 className="text-2xl font-display font-bold text-ivory mb-4 group-hover:text-ai-teal transition-colors" data-id="element-459">
                  {t(`solutions.${key}.title`)}
                </h2>
                <p className="text-gray-400 mb-8 leading-relaxed" data-id="element-460">
                  {t(`solutions.${key}.desc`)}
                </p>
                <ul className="space-y-3 mb-8" data-id="element-461">
                  {[1, 2, 3].map(num => <li key={num} className="flex items-center text-sm font-mono text-graphite group-hover:text-gray-400 transition-colors" data-id="element-462">
                      <ChevronRight className="w-4 h-4 text-muted-gold mr-2" data-id="element-463" />
                      {t(`solutions.${key}.feat${num}`)}
                    </li>)}
                </ul>

                <button className="text-xs font-bold uppercase tracking-widest text-ivory border-b border-transparent group-hover:border-ai-teal transition-all pb-1" data-id="element-464">
                  {t('solutions.cta')}
                </button>
              </article>)}
          </div>

          {/* New Strategic Solutions Section */}
          <section className="border-t border-white/10 pt-32" data-id="element-465">
            <div className="mb-20 animate-fade-in-up" data-id="element-466">
              <h2 className="text-4xl md:text-6xl font-display font-bold text-ivory mb-6" data-id="element-467">
                STRATEGIC SOLUTIONS
              </h2>
              <p className="text-xl text-gray-400 max-w-3xl border-l-2 border-muted-gold pl-6" data-id="element-468">
                Bespoke technological systems for high-stakes and
                future-critical environments.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-469">
              {strategicSolutions.map((solution, idx) => {
              const Icon = solution.icon;
              return <article key={idx} className={`group bg-white/[0.02] border border-white/10 p-8 hover:border-${solution.color}/30 hover:bg-white/[0.04] transition-all duration-500 animate-fade-in-up delay-${idx * 100} relative overflow-hidden`} data-id="element-470">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-2xl" data-id="element-471" />

                    <div className="relative z-10" data-id="element-472">
                      <div className="mb-6 p-3 bg-white/5 inline-flex rounded border border-white/10 group-hover:border-white/20 transition-colors" data-id="element-473">
                        <Icon className={`w-7 h-7 text-${solution.color}`} data-id="element-474" />
                      </div>

                      <h3 className="text-xl font-display font-bold text-ivory mb-4 group-hover:text-ai-teal transition-colors" data-id="element-475">
                        {solution.title}
                      </h3>

                      <p className="text-gray-400 text-sm leading-relaxed group-hover:text-gray-300 transition-colors" data-id="element-476">
                        {solution.description}
                      </p>

                      <div className="mt-6 h-px w-0 group-hover:w-full bg-gradient-to-r from-transparent via-ai-teal to-transparent transition-all duration-700" data-id="element-477" />
                    </div>
                  </article>;
            })}
            </div>
          </section>
        </div>
      </div>
    </>;
}